/**
 * @file useIdleSession.js
 * @description Watches for an idle session, warns before it ends, and ends it.
 *
 * ── The bug this exists for ─────────────────────────────────────────────
 *
 * The API client already refreshes tokens and already redirects when a refresh
 * fails. Both are correct, and both are REACTIVE: they only run when a request
 * is made. Nothing watches an idle tab.
 *
 * So somebody who stopped typing stayed on a screen that kept offering every
 * action they no longer had a session for. The moment they finally clicked,
 * the request 401'd, the refresh failed, and they were thrown to the login
 * page mid-gesture — which is the "it starts flashing and I was logged out
 * without touching anything" report. The session had ended minutes earlier;
 * the interface was the last to know.
 *
 * ── What it does ────────────────────────────────────────────────────────
 *
 * Tracks real interaction, warns while there is still time to say "keep me
 * signed in", and only then ends the session. The warning is the whole point:
 * a countdown somebody can cancel is a very different experience from being
 * ejected, even though the timings are identical.
 *
 * ── Deliberate decisions ────────────────────────────────────────────────
 *
 * ACTIVITY IS LISTENED FOR PASSIVELY, and throttled to once a second. Mousemove
 * fires hundreds of times a second; resetting a timer on every one would make
 * this hook the most expensive thing on the page.
 *
 * ACTIVITY IS SHARED ACROSS TABS. It used to be counted per tab, and a session
 * is not per tab: a background tab nobody had touched for thirty minutes timed
 * out and signed out the session the person was busy using in the tab next to
 * it. Every tab now writes its last activity to one localStorage timestamp and
 * reads the newest before deciding anything, so no tab expires while ANY tab
 * is in use. A sign-out in one tab reaches the others through the same
 * channel (`onSignedOutElsewhere`). See modules/auth/sessionSignals.js.
 *
 * A HIDDEN TAB DOES NOT COUNT AS ACTIVITY. A tab left open on a second monitor
 * is not somebody working — but it no longer expires on its own clock either.
 *
 * IT DOES NOT REFRESH TOKENS. That is the API client's job and it does it
 * well. Two things renewing a session on different clocks would drift, and the
 * one that is wrong would be invisible.
 *
 * THE TIMEOUT IS A PARAMETER, not a constant read in here. Where it comes from
 * is the caller's business — today a default, and an operator setting when one
 * exists — because a security timeout that can only be changed by a deploy is
 * a timeout nobody will ever tune to their own risk.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LAST_ACTIVITY_KEY,
  SIGN_OUT_SIGNAL_KEY,
  latestActivity,
  parseSignOutSignal,
  publishActivity,
  signalAppliesHere
} from '../modules/auth/sessionSignals';

// Sensible defaults. Thirty minutes idle is long enough not to interrupt
// somebody reading a long report, and short enough to matter on a shared
// machine; sixty seconds is long enough to notice a dialog and reach for the
// mouse without being so long that the warning becomes background noise.
export const DEFAULT_IDLE_MS = 30 * 60 * 1000;
export const DEFAULT_WARN_MS = 60 * 1000;

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'pointerdown'];

export function useIdleSession({
  enabled = true,
  idleMs = DEFAULT_IDLE_MS,
  warnMs = DEFAULT_WARN_MS,
  onExpire,
  // Called with `{ reason }` when another tab of this browser ended the session.
  onSignedOutElsewhere
} = {}) {
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const lastActivity = useRef(Date.now());
  const throttleUntil = useRef(0);
  // Held in a ref so changing the callback cannot restart the timers — a
  // parent re-render must not silently extend somebody's session.
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const onSignedOutElsewhereRef = useRef(onSignedOutElsewhere);
  onSignedOutElsewhereRef.current = onSignedOutElsewhere;

  const staySignedIn = useCallback(() => {
    const now = Date.now();
    lastActivity.current = now;
    publishActivity(now);
    setWarning(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setWarning(false);
      return undefined;
    }

    // Fired once per signed-in period. The interval keeps ticking while the
    // caller's async logout runs, and calling onExpire every second sent a
    // /logout request (and a navigation) per tick.
    let expired = false;

    // Arriving in a tab (signing in, opening a page) is activity for every tab.
    const start = Date.now();
    lastActivity.current = start;
    publishActivity(start);

    const bump = () => {
      const now = Date.now();
      if (now < throttleUntil.current) return;
      throttleUntil.current = now + 1000;
      lastActivity.current = now;
      publishActivity(now);
    };

    // `passive` because none of these handlers call preventDefault, and a
    // non-passive scroll listener blocks scrolling on touch devices.
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, bump, { passive: true });
    }

    // Fired in OTHER tabs only. Activity is also re-read on every tick, so
    // this is for promptness (a warning clears within the second), not for
    // correctness.
    const onStorage = (e) => {
      if (e.key === LAST_ACTIVITY_KEY) {
        lastActivity.current = latestActivity(lastActivity.current);
      } else if (e.key === SIGN_OUT_SIGNAL_KEY && e.newValue) {
        const signal = parseSignOutSignal(e.newValue);
        if (signalAppliesHere(signal)) {
          setWarning(false);
          onSignedOutElsewhereRef.current?.(signal);
        }
      }
    };
    window.addEventListener('storage', onStorage);

    // One second is precise enough for a countdown a person reads, and cheap.
    // A timer per event would be accurate to the millisecond and pointless.
    const tick = setInterval(() => {
      // The newest activity in any tab, not just this one.
      lastActivity.current = latestActivity(lastActivity.current);
      const idleFor = Date.now() - lastActivity.current;
      const remaining = idleMs - idleFor;

      if (remaining <= 0) {
        if (expired) return;
        expired = true;
        setWarning(false);
        onExpireRef.current?.();
        return;
      }
      if (remaining <= warnMs) {
        setWarning(true);
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else if (remaining > warnMs) {
        // Cleared rather than left standing: activity during the warning
        // window means they are here, and a dialog that lingers after the
        // danger passed teaches people to dismiss it without reading.
        setWarning((w) => (w ? false : w));
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      window.removeEventListener('storage', onStorage);
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, bump);
    };
  }, [enabled, idleMs, warnMs]);

  return { warning, secondsLeft, staySignedIn };
}

export default useIdleSession;
